import { heightChangeMaterialMap, MaterialMap } from "@biz/definitions/MaterialMap";
import { HeightChangeType, ISunSailData, MaterialGroup, MaterialType, PoleId, PoleIndex, RemoteType } from "@biz/definitions/SunSailDefinitions";
import { ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";
import { PoleLogic } from "./PoleLogic";
import { SunSailLogic } from "./SunSailLogic";

export interface IHeightChangeSlideInfo {
  length: number;
  scale: number;
  rollOffsetX: number;
  rollOffsetY: number;
  rollRadius: number;
  offsetSecond: number;
  articles: string[];
  partListName: string;
  partListAdditional: string;
}

export interface IHeightChangeSandyInfo {
  offsetX: number;
}

type SchlittenType = "Schlitten1" | "Schlitten2" | "Schlitten3" | "Schlitten4";

const heightChangeSlideBars: Record<SchlittenType, IHeightChangeSlideInfo> = {
  Schlitten1: { // mit Doppelrolle X
    length: 120,
    scale: 1.13,
    rollOffsetX: 0.05,
    rollOffsetY: 0.025,
    rollRadius: 0.0125,
    offsetSecond: 0.05,
    articles: ["00E13001866", "00E13001866", "00E13001893", "00E13001895"],
    partListName: "Gleitschlitten mit <mark><b>Doppelrolle</b></mark>",
    partListAdditional: "<mark><b>Doppelrolle</b></mark>"
  },
  Schlitten2: {  // mit Doppelrolle und Drehrad
    length: 120,
    scale: 1.13,
    rollOffsetX: 0.05,
    rollOffsetY: 0.025,
    rollRadius: 0.0125,
    offsetSecond: 0.05,
    articles: ["00E13000204", "00E13000204", "00E13001690", "00E13001681"],
    partListName: "Gleitschlitten mit <mark><b>Doppelrolle und Seilbremse</b></mark>",
    partListAdditional: "<mark><b>Doppelrolle und Seilbremse</b></mark>"
  },
  Schlitten3: { // mit Rolle
    length: 65,
    scale: 1.0,
    rollOffsetX: 0.05,
    rollOffsetY: 0.0175,
    rollRadius: 0.0125,
    offsetSecond: 0.0,
    articles: ["00E13000052", "00E13000027", "00E13000764", "00E13000603"],
    partListName: "Gleitschlitten mit <mark><b>Einzelrolle</b></mark>",
    partListAdditional: "<mark><b>Einzelrolle</b></mark>"
  },
  Schlitten4: { // mit Bügel
    length: 65,
    scale: 1.0,
    rollOffsetX: 0.02,
    rollOffsetY: 0.022,
    rollRadius: 0.0,
    offsetSecond: 0,
    articles: ["00E13001865", "00E13001865", "00E13001894", "00E13001896"],
    partListName: "Gleitschlitten mit <mark><b>Bügel</b></mark>",
    partListAdditional: "<mark><b>Bügel</b></mark>"
  },
}

const heightChangeSlideBarMap: Record<string, SchlittenType> = {
}
for (const a of heightChangeSlideBars["Schlitten1"].articles) {
  heightChangeSlideBarMap[a] = "Schlitten1";
}
for (const a of heightChangeSlideBars["Schlitten2"].articles) {
  heightChangeSlideBarMap[a] = "Schlitten2";
}
for (const a of heightChangeSlideBars["Schlitten3"].articles) {
  heightChangeSlideBarMap[a] = "Schlitten3";
}
for (const a of heightChangeSlideBars["Schlitten4"].articles) {
  heightChangeSlideBarMap[a] = "Schlitten4";
}

export interface IHeightChangeWithMotor {
  remoteType: RemoteType | undefined;
  materialType: MaterialGroup;
}

const heightChangesWithMotor: Record<string, IHeightChangeWithMotor> = {
  "00E13001925": {
    materialType: "alu",
    remoteType: "centronic+"
  },
  "00E13001926": {
    materialType: "steel",
    remoteType: "centronic+"
  },
  "00E13001927": {
    materialType: "droppole",
    remoteType: "centronic+"
  },
  "00E13001975": {
    materialType: "droppole",
    remoteType: "centronic+"
  },
  "00E13000974": {
    materialType: "alu",
    remoteType: "centronic"
  },
  "00E13000975": {
    materialType: "steel",
    remoteType: "centronic"
  },
  "00E13000976": {
    materialType: "droppole",
    remoteType: "centronic"
  },
  "00E13001973": {
    materialType: "droppole",
    remoteType: "centronic"
  },
  "00E13001056": {
    materialType: "alu",
    remoteType: undefined
  },
  "00E13001057": {
    materialType: "steel",
    remoteType: undefined
  },
  "00E13001058": {
    materialType: "droppole",
    remoteType: undefined
  },
  "00E13001974": {
    materialType: "droppole",
    remoteType: undefined
  },
}

enum HeightChangeMaterialIndex {
  Alu = 0,
  Steel = 1,
  Droppole = 2,
  Wall = 3
}

export class HeightChangeLogic {
  static getMaterialIndex(material: MaterialType | undefined): HeightChangeMaterialIndex {
    if (PoleLogic.isDroppole(material))
      return HeightChangeMaterialIndex.Droppole;
    if (PoleLogic.isSteel(material))
      return HeightChangeMaterialIndex.Steel;
    if (PoleLogic.isAlu(material))
      return HeightChangeMaterialIndex.Alu;
    if (!material)
      return HeightChangeMaterialIndex.Wall;

    return HeightChangeMaterialIndex.Wall;
  }

  static getSlideBarArticle(schlitten: SchlittenType, material: MaterialType | undefined): string | undefined {
    const materialIndex = HeightChangeLogic.getMaterialIndex(material);
    return HeightChangeLogic.getSlideInfoForSchlitten(schlitten)?.articles[materialIndex];
  }

  static getSlideNameForSunSail(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): SchlittenType | undefined {
    const articleId = SunSailLogic.getHeightChangeArticle(sunSail, poleId)
    if (!articleId)
      return undefined;

    return HeightChangeLogic.getSlideNameForArticle(articleId);
  }

  static getSlideNameForArticle(articleNumber: string | undefined): SchlittenType | undefined {
    if (!articleNumber)
      return undefined;

    return heightChangeSlideBarMap[articleNumber];
  }

  static getSlideInfo(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): IHeightChangeSlideInfo | undefined {
    const name = HeightChangeLogic.getSlideNameForSunSail(sunSail, poleIndex);
    return name ? HeightChangeLogic.getSlideInfoForSchlitten(name) : undefined;
  }

  static getSandyInfo(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): IHeightChangeSandyInfo | undefined {
    const article = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    if (!article)
      return undefined;
    return HeightChangeLogic.getSandyInfoForArticle(article);
  }

  static getSandyInfoForArticle(article: string): IHeightChangeSandyInfo | undefined {
    if (!article)
      return undefined;
    const ret: Record<string, IHeightChangeSandyInfo> = {
      "60003A003": { offsetX: -0.06 },
      "60003A001": { offsetX: -0.04 },
      "60003A002": { offsetX: -0.04 },
    }
    return ret[article];
  }

  static getSlideInfoForSchlitten(schlitten: SchlittenType): IHeightChangeSlideInfo | undefined {
    return heightChangeSlideBars[schlitten];
  }

  static getSlideInfoForSunSail(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): IHeightChangeSlideInfo | undefined {
    const slideName = HeightChangeLogic.getSlideNameForSunSail(sunSail, poleId);
    return slideName ? heightChangeSlideBars[slideName] : undefined;
  }

  static hasHeightChangeMotor(articleId: string | undefined): boolean {
    if (!articleId)
      return false;
    return !!heightChangesWithMotor[articleId];
  }

  static getHeightChangeType(sunSail: ISunSailData, poleIndex: number): HeightChangeType | undefined {
    const heightChangeArticle = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    if (!heightChangeArticle)
      return undefined;
    if (MaterialMap.includes([heightChangeMaterialMap.sandyPatent, heightChangeMaterialMap.sandyWire], heightChangeArticle))
      return "sandy";
    if (HeightChangeLogic.hasHeightChangeMotor(heightChangeArticle))
      return "motor";
    else if (["71006A001", "00E13001874", "00E13001875"].includes(heightChangeArticle))
      return "pulley";
    else if (HeightChangeLogic.getSlideInfoForSunSail(sunSail, poleIndex))
      return "slide";
    return "winch";
  }

  static getHeightChangeRemoteType(articleId: string | undefined): RemoteType | undefined {
    if (!articleId)
      return undefined;
    return heightChangesWithMotor[articleId]?.remoteType;
  }
}